import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';

// A convenience, not state we own: a browser that refuses storage must still render the sidebar.
const readStored = (key) => {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const hasCount = (count) => typeof count === 'number' && Number.isFinite(count);
const formatCount = (count) => count.toLocaleString('fr-FR');

// Collapsed, the pill is gone: the tooltip carries the figure.
const itemTitle = (item) => {
  const title = item.title || (typeof item.label === 'string' ? item.label : undefined);
  return title && hasCount(item.count) ? `${title} (${formatCount(item.count)})` : title;
};

/** Figure next to an item (reports, analyses, photos…). 0 is a figure too: only null hides it. */
const CountPill = ({ count, active }) => (
  <span
    className={cx(
      'min-w-[1.5rem] flex-shrink-0 rounded-full px-1.5 py-0.5 text-center text-[11px] font-semibold leading-4 tabular-nums',
      active
        ? 'bg-brand-200 text-brand-700 dark:bg-brand-800 dark:text-brand-100'
        : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300',
    )}
  >
    {formatCount(count)}
  </span>
);

/**
 * Left navigation of the viewers (Insights GPS / Visits sidebar), md and up.
 * `sections`: [{ id, label?, items: [{ id, label, icon, to, active, count?, badge?, title? }] }].
 * `count` (number) renders a figure pill, `badge` any node (e.g. AdminBadge, desktop only).
 * `collapsible` + `storageKey` add the icons-only mode, remembered per browser.
 */
export const ViewerSidebar = ({
  header = null,
  sections = [],
  footer = null,
  navLabel = 'Navigation',
  collapsible = false,
  storageKey = null,
}) => {
  const [collapsed, setCollapsed] = useState(() => collapsible && readStored(storageKey));

  useEffect(() => {
    if (!collapsible || !storageKey) return;
    try {
      window.localStorage.setItem(storageKey, collapsed ? '1' : '0');
    } catch {
      // The sidebar just forgets between sessions.
    }
  }, [collapsed, collapsible, storageKey]);

  const isCollapsed = collapsible && collapsed;
  const hasItems = sections.some((s) => s.items?.length);

  // Nothing to navigate: no empty column.
  if (!header && !hasItems && !footer) return null;

  return (
    <aside
      className={cx(
        'hidden h-full flex-shrink-0 flex-col gap-4 overflow-y-auto overflow-x-hidden border-r border-gray-200 bg-white transition-[width] duration-150 dark:border-gray-900 dark:bg-night-850 md:flex',
        isCollapsed ? 'w-16 px-2 py-3' : 'w-64 p-4',
      )}
    >
      {!isCollapsed && header}

      {collapsible && hasItems && (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          title={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          className={cx(
            'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium text-gray-500 outline-none hover:bg-gray-100 hover:text-gray-800 focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200',
            isCollapsed ? 'justify-center' : 'self-end',
          )}
        >
          {isCollapsed ? <LuChevronRight className="h-4 w-4" aria-hidden /> : <LuChevronLeft className="h-4 w-4" aria-hidden />}
          {!isCollapsed && <span>Replier</span>}
        </button>
      )}

      <nav className="space-y-5" aria-label={navLabel}>
        {sections.filter((s) => s.items?.length).map((section) => (
          <div key={section.id} className="space-y-1">
            {section.label && !isCollapsed && (
              <p className="truncate px-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{section.label}</p>
            )}
            {section.label && isCollapsed && <div className="mx-2 border-t border-gray-100 pt-1 dark:border-gray-800" aria-hidden />}
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.to}
                  aria-current={item.active ? 'page' : undefined}
                  title={isCollapsed ? itemTitle(item) : undefined}
                  className={cx(
                    'group flex w-full items-center gap-2.5 rounded-lg py-2.5 text-sm font-medium outline-none transition-all',
                    'focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-night-850',
                    isCollapsed ? 'justify-center px-2' : 'px-3',
                    item.active
                      ? 'bg-brand-50 font-semibold text-brand-600 dark:bg-brand-900 dark:text-brand-400'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white',
                  )}
                >
                  {Icon && (
                    <Icon className={cx('h-[18px] w-[18px] flex-shrink-0', item.active ? 'text-brand-600 dark:text-brand-400' : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300')} aria-hidden />
                  )}
                  <span className={isCollapsed ? 'sr-only' : 'min-w-0 flex-1 truncate text-left'}>{item.label}</span>
                  {!isCollapsed && hasCount(item.count) && <CountPill count={item.count} active={item.active} />}
                  {!isCollapsed && item.badge}
                  {item.active && !isCollapsed && <LuChevronRight className="h-4 w-4 flex-shrink-0 opacity-70" aria-hidden />}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      {footer && !isCollapsed && <div className="mt-auto">{footer}</div>}
    </aside>
  );
};

/** Same destinations below md: header on top, then a horizontally scrolling pill bar. */
export const ViewerMobileNav = ({ header = null, sections = [], navLabel = 'Navigation' }) => {
  const items = sections.flatMap((s) => s.items || []);
  return (
    <div className="space-y-3 md:hidden">
      {header}
      {items.length > 0 && (
        <nav aria-label={navLabel} className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 scrollbar-none">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.to}
                aria-current={item.active ? 'page' : undefined}
                className={cx(
                  'inline-flex min-h-[40px] flex-shrink-0 items-center gap-1.5 rounded-full border px-3.5 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  item.active
                    ? 'border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-800 dark:bg-brand-900 dark:text-brand-300'
                    : 'border-gray-200 bg-white text-gray-600 dark:border-gray-800 dark:bg-night-800 dark:text-gray-300',
                )}
              >
                {Icon && <Icon className="h-4 w-4" aria-hidden />}
                {item.label}
                {hasCount(item.count) && <CountPill count={item.count} active={item.active} />}
              </Link>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export default ViewerSidebar;
